import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import authService from '../services/authService';
import logo from '../Logo.svg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');

  // Forgot-password state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Reset-password state
  const [resetData, setResetData] = useState({ newPassword: '', confirmPassword: '' });
  const [resetErrors, setResetErrors] = useState({});

  const [isLoading, setIsLoading] = useState(false);

  // --- Forgot form ---
  const validateEmail = (val) => {
    if (!val.trim()) return 'Email is required.';
    if (!EMAIL_RE.test(val.trim())) return 'Enter a valid email address.';
    return '';
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }
    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
      setEmailSent(true);
      toast.success('Password reset link sent to your email');
    } catch (error) {
      toast.error(error || 'Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Reset form ---
  const validateReset = (data) => {
    const e = {};
    if (!data.newPassword) e.newPassword = 'Password is required.';
    else if (data.newPassword.length < 8) e.newPassword = 'Password must be at least 8 characters.';
    if (!data.confirmPassword) e.confirmPassword = 'Please confirm your password.';
    else if (data.newPassword && data.newPassword !== data.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    return e;
  };

  const handleResetChange = (e) => {
    const updated = { ...resetData, [e.target.name]: e.target.value };
    setResetData(updated);
    if (resetErrors[e.target.name]) setResetErrors(prev => ({ ...prev, [e.target.name]: undefined }));
    if (e.target.name === 'newPassword' && resetErrors.confirmPassword) {
      setResetErrors(prev => ({ ...prev, confirmPassword: undefined }));
    }
  };

  const handleResetBlur = (e) => {
    const errs = validateReset(resetData);
    if (errs[e.target.name]) setResetErrors(prev => ({ ...prev, [e.target.name]: errs[e.target.name] }));
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    const errs = validateReset(resetData);
    if (Object.keys(errs).length) { setResetErrors(errs); return; }
    setIsLoading(true);
    try {
      await authService.resetPassword(resetToken, resetData.newPassword);
      toast.success('Password reset successfully! Please log in.');
      navigate('/login');
    } catch (error) {
      toast.error(error || 'Failed to reset password. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = (hasError) => hasError ? 'border-destructive focus-visible:ring-destructive' : '';

  // Reset-password form
  if (resetToken) {
    return (
      <div className="min-h-screen gradient-hero flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <Card className="w-full max-w-md animate-scale-in">
            <CardHeader className="space-y-4">
              <div className="flex justify-center">
                <Link to="/">
                  <img src={logo} alt="Verdionix" className="h-14 w-auto" style={{ mixBlendMode: 'multiply' }} />
                </Link>
              </div>
              <div className="text-center">
                <CardTitle className="text-2xl font-display">Set New Password</CardTitle>
                <CardDescription>Enter your new password below</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetSubmit} className="space-y-4" noValidate>
                <div className="space-y-1">
                  <Label htmlFor="newPassword">New Password <span className="text-destructive">*</span></Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    placeholder="At least 8 characters"
                    value={resetData.newPassword}
                    onChange={handleResetChange}
                    onBlur={handleResetBlur}
                    className={inputCls(resetErrors.newPassword)}
                  />
                  {resetErrors.newPassword && <p className="text-xs text-destructive mt-0.5">{resetErrors.newPassword}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="confirmPassword">Confirm Password <span className="text-destructive">*</span></Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Repeat your new password"
                    value={resetData.confirmPassword}
                    onChange={handleResetChange}
                    onBlur={handleResetBlur}
                    className={inputCls(resetErrors.confirmPassword)}
                  />
                  {resetErrors.confirmPassword && <p className="text-xs text-destructive mt-0.5">{resetErrors.confirmPassword}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Resetting…' : 'Reset Password'}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition">← Back to Login</Link>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Forgot-password form
  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <Card className="w-full max-w-md animate-scale-in">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <Link to="/">
                <img src={logo} alt="Verdionix" className="h-14 w-auto" style={{ mixBlendMode: 'multiply' }} />
              </Link>
            </div>
            <div className="text-center">
              <CardTitle className="text-2xl font-display">Reset Password</CardTitle>
              <CardDescription>
                {emailSent
                  ? 'Check your email for reset instructions'
                  : 'Enter your email to receive a password reset link'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {!emailSent ? (
              <form onSubmit={handleForgotSubmit} className="space-y-4" noValidate>
                <div className="space-y-1">
                  <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    onBlur={() => setEmailError(validateEmail(email))}
                    className={inputCls(emailError)}
                  />
                  {emailError && <p className="text-xs text-destructive mt-0.5">{emailError}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending…' : 'Send Reset Link'}
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  We've sent a password reset link to <strong>{email}</strong>. Please check your
                  inbox and follow the instructions.
                </p>
                <Button onClick={() => navigate('/login')} className="w-full">
                  Back to Login
                </Button>
              </div>
            )}
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition">← Back to Login</Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
